/**
 * Loading component for Suspense fallback
 * Provides a minimal loading spinner that matches the site design system
 */

interface LoadingProps {
  /** Optional message to display below the spinner */
  message?: string;
  /** Full screen overlay (true) or inline (false) */
  fullScreen?: boolean;
}

export function Loading({ message, fullScreen = true }: LoadingProps) {
  const spinner = (
    <div className="flex flex-col items-center justify-center gap-4">
      {/* Spinner using accent color */}
      <div className="relative w-12 h-12">
        <div
          className="absolute inset-0 rounded-full border-4 border-cream"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 rounded-full border-4 border-accent border-t-transparent animate-spin"
          aria-hidden="true"
        />
      </div>
      {message && (
        <p className="text-muted text-sm font-medium">{message}</p>
      )}
      <span className="sr-only">Loading...</span>
    </div>
  );

  if (fullScreen) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-paper/80 backdrop-blur-sm"
        role="status"
        aria-live="polite"
      >
        {spinner}
      </div>
    );
  }

  return (
    <div
      className="flex min-h-[200px] w-full items-center justify-center py-12"
      role="status"
      aria-live="polite"
    >
      {spinner}
    </div>
  );
}

/**
 * Page loading skeleton for route transitions
 * Shows a subtle loading state while lazy routes load
 */
export function PageLoadingFallback() {
  return (
    <div className="min-h-screen bg-paper">
      {/* Header placeholder */}
      <div className="h-16 bg-white/50 backdrop-blur-sm border-b border-cream" />

      {/* Content loading state */}
      <div className="container-marketing py-16">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Skeleton heading */}
          <div className="h-12 w-3/4 bg-cream rounded-lg animate-pulse" />

          {/* Skeleton paragraphs */}
          <div className="space-y-3">
            <div className="h-4 w-full bg-cream rounded animate-pulse" />
            <div className="h-4 w-5/6 bg-cream rounded animate-pulse" />
            <div className="h-4 w-4/5 bg-cream rounded animate-pulse" />
          </div>

          {/* Skeleton content blocks */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-8">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-32 bg-cream rounded-xl animate-pulse"
                style={{ animationDelay: `${i * 100}ms` }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
